import type { ReactNode } from "react";
import { noop } from "lodash";
import { t } from "@/i18n-js/instance";
import {
  SearchableSelect,
  filterByLabel,
  useSearchableSelect,
} from "@circle-react-uikit/SearchableSelect";
import { CreateLinkOption } from "./CreateLinkOption";
import { GroupedOptions } from "./GroupedOptions";
import { RegularOptions } from "./RegularOptions";

export interface TextSelectOption {
  label: string;
  value: string | number | null;
}

export interface TextSelectProps<
  Option extends TextSelectOption = TextSelectOption,
> {
  buttonClassName?: string;
  createLinkLabel?: string;
  disabled?: boolean;
  displayValue?: (option: Option) => string;
  emptyMessage?: string;
  filterBy?: (
    options: Option[] | readonly Option[],
    search: string,
  ) => Option[];
  groupBy?: (option: Option) => string | undefined;
  hideCaret?: boolean;
  initialValue?: Option["value"];
  isError?: boolean;
  label?: ReactNode;
  onChange?: (value: Option["value"]) => void;
  onCreateLinkClick?: (searchTerm: string) => void;
  onSearchChange?: (search: string) => void;
  options?: readonly Option[];
  placeholder?: string;
  value?: Option["value"];
}

export const TextSelect = <Option extends TextSelectOption = TextSelectOption>({
  options = [],
  onChange = noop,
  onSearchChange = noop,
  isError = false,
  placeholder = t("select_an_option"),
  emptyMessage = t("no_options"),
  filterBy = filterByLabel,
  groupBy,
  displayValue = option => option?.label || "",
  initialValue = "",
  value,
  createLinkLabel,
  onCreateLinkClick,
  ...rest
}: TextSelectProps<Option>) => {
  const {
    search,
    selectProps,
    searchResults,
    hasNoResults,
    groupedSearchResults,
    hasGroups,
  } = useSearchableSelect<Option, Option["value"]>({
    isError,
    options,
    onChange,
    filterBy,
    placeholder,
    displayValue,
    onSearchChange,
    groupByValue: groupBy,
    initialValue,
    value,
  });

  return (
    <SearchableSelect {...rest} {...selectProps}>
      {hasNoResults && !createLinkLabel && (
        <SearchableSelect.EmptyOption label={emptyMessage} />
      )}
      {!hasNoResults && !hasGroups && (
        <RegularOptions options={searchResults} />
      )}
      {!hasNoResults && hasGroups && groupedSearchResults && (
        <GroupedOptions groupedOptions={groupedSearchResults} />
      )}
      {createLinkLabel && onCreateLinkClick && (
        <>
          {!hasNoResults && <p className="border-primary mb-2 border-b" />}
          <CreateLinkOption
            search={search}
            createLinkLabel={createLinkLabel}
            onClick={() => onCreateLinkClick(search)}
          />
        </>
      )}
    </SearchableSelect>
  );
};
